// =============================================================================
// useWebPush — enregistre le navigateur de l'admin pour recevoir des Web Push.
// -----------------------------------------------------------------------------
// À appeler avec l'userId de l'admin connecté. Le hook :
//   1. vérifie le support navigateur + la permission de notification
//   2. enregistre le service worker FCM
//   3. récupère le token web (getToken + clé VAPID)
//   4. stocke ce token sur admin_Profile.fcm_web_token (pour le trigger SQL)
//   5. affiche les pushs reçus quand l'onglet est au premier plan (onMessage)
// =============================================================================

import { useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";
import {
  getMessagingIfSupported,
  getToken,
  onMessage,
  VAPID_KEY,
} from "../config/firebase";

async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/firebase-messaging-sw.js");
  } catch (e) {
    console.warn("[webpush] SW register failed:", e);
    return null;
  }
}

function showForeground(payload) {
  const d = payload.data || {};
  const title = d.title || "Batilink";
  const body = d.body || "";
  // L'onglet est ouvert : on notifie quand même via une CustomEvent (toast
  // possible côté UI) + une notification système si la permission le permet.
  try {
    window.dispatchEvent(
      new CustomEvent("webpush:foreground", { detail: { title, body, ...d } })
    );
  } catch {}
  try {
    if (Notification.permission === "granted") {
      new Notification(title, { body, icon: d.icon || "/favicon.png" });
    }
  } catch {}
}

export function useWebPush(userId) {
  const startedRef = useRef(false);

  useEffect(() => {
    if (!userId || startedRef.current) return;
    startedRef.current = true;

    let cancelled = false;
    let unsubscribeOnMessage = null;

    (async () => {
      const messaging = await getMessagingIfSupported();
      if (!messaging) {
        console.warn("[webpush] messaging NON supporté (isSupported=false). Navigateur incompatible ou config Firebase invalide.");
        return;
      }
      if (cancelled) return;

      // 1. Permission. On ne (re)demande que si l'utilisateur n'a pas encore
      //    choisi ("default"). S'il a refusé, on n'insiste pas.
      let permission = Notification.permission;
      if (permission === "default") {
        permission = await Notification.requestPermission();
      }
      if (permission !== "granted") {
        console.warn("[webpush] permission non accordée (" + permission + ") → arrêt. Débloquez les notifications dans le cadenas 🔒 de l'URL.");
        return;
      }
      if (cancelled) return;

      // 2-3. Service worker + token web
      const swReg = await registerServiceWorker();
      let token = null;
      try {
        token = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: swReg || undefined,
        });
      } catch (e) {
        console.error("[webpush] getToken FAILED:", e);
        return;
      }
      if (!token) {
        console.warn("[webpush] getToken a renvoyé un token VIDE.");
        return;
      }
      if (cancelled) return;

      // 4. Stocker le token dans admin_web_tokens (1 ligne / navigateur →
      //    gère plusieurs PC pour le même admin). upsert sur `token` (unique) :
      //    même PC = met à jour, autre PC = nouvelle ligne.
      try {
        const { error } = await supabase
          .from("admin_web_tokens")
          .upsert(
            {
              user_id: userId,
              token,
              user_agent:
                typeof navigator !== "undefined" ? navigator.userAgent : null,
              updated_at: new Date().toISOString(),
            },
            { onConflict: "token" }
          );
        if (error) {
          console.error("[webpush] store token ERROR:", error);
        }
      } catch (e) {
        console.error("[webpush] store token FAILED:", e);
      }

      // 5. Premier plan
      unsubscribeOnMessage = onMessage(messaging, showForeground);
    })();

    return () => {
      cancelled = true;
      if (typeof unsubscribeOnMessage === "function") unsubscribeOnMessage();
    };
  }, [userId]);
}

export default useWebPush;
